import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";

export default class UtilStorage {
  private static readonly AUTH_KEYS = [
    "token",
    "secret_key",
    "refresh_token",
    "user_data",
    "phone",
    "phoneRegisterYandex",
    "email",
  ];

  public static async setItem(key: string, value: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  }

  public static async getItem(key: string): Promise<string | null> {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key });
      return value;
    }
    return localStorage.getItem(key);
  }

  public static async setObject(key: string, value: object): Promise<void> {
    return this.setItem(key, JSON.stringify(value));
  }

  public static async getObject<T>(key: string): Promise<T | null> {
    const value = await this.getItem(key);
    try {
      return value ? (JSON.parse(value) as T) : null;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  public static async removeItem(key: string): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  }

  public static async clearAuthData(): Promise<void> {
    await Promise.all(this.AUTH_KEYS.map((key) => this.removeItem(key)));

    if (!Capacitor.isNativePlatform()) {
      document.cookie =
        "token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      document.cookie =
        "refresh_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
    }
  }

  public static async getAllKeys(): Promise<string[]> {
    if (Capacitor.isNativePlatform()) {
      const { keys } = await Preferences.keys();
      return keys;
    }
    return Object.keys(localStorage);
  }

  public static async clearAll(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Preferences.clear();
    } else {
      localStorage.clear();
    }
  }

  public static async migrateLegacyStorage(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    const legacyKeys = await this.getAllKeys();
    const authKeys = this.AUTH_KEYS;

    await Promise.all(
      legacyKeys.map(async (key) => {
        if (authKeys.includes(key)) {
          const value = localStorage.getItem(key);
          if (value) {
            await this.setItem(key, value);
            localStorage.removeItem(key);
          }
        }
      }),
    );
  }
}
